import BadgeImage from '@/components/BadgeCard/BadgeImage';
import BadgeDetailHeader from '@/components/BadgeDetailHeader/BadgeDetailHeader';
import {BadgeRewardsList} from '@/components/BadgeRewardsList/BadgeRewardsList';
import ButtonRaw from '@/components/Buttons/ButtonRaw';
import {BadgeActionLockIcon} from '@/components/Icons';
import {BADGE_STATUSES} from '@/consts/loyalty/badgeStatuses';
import useBadgeDetailRoute from '@/hooks/useBadgeDetailRoute';
import useJourneyNotification from '@/hooks/useJourneyNotification';
import {getBadgeDetailsContent} from '@/services/content/appContent';
import type {Badge, JourneyLevelBadge} from '@/types/loyalty';
import {twx} from '@/utils/tailwindUtils';

type MultiLevelBadgeDetailsProps = {
    badge: Badge;
    titleId?: string;
};

type LevelCardProps = {
    level: JourneyLevelBadge;
    fullWidth: boolean;
    isFirst?: boolean;
    onPress?: () => void;
};

const LevelCard = ({level, fullWidth, isFirst, onPress}: LevelCardProps) => {
    const {displayName, badgeDescription, imageUrl, status} = level;
    const isLocked = status === BADGE_STATUSES.LOCKED;
    // First level is always reachable — clickable and never shows the lock.
    const isClickable = !isLocked || isFirst;
    const showLock = isLocked && !isFirst;

    const className = twx(
        'relative flex flex-col items-center gap-4 rounded-xl bg-charcoal p-4 text-center text-romance',
        fullWidth && 'col-span-2',
        isClickable && 'w-full cursor-pointer border-0 outline-none focus-visible:ring-2 focus-visible:ring-white/40'
    );

    const content = (
        <>
            <BadgeImage imageUrl={imageUrl} size={122} />
            <div className='flex flex-col gap-2'>
                <span className='m-0 font-bold text-18 leading-13 tracking-02'>{displayName}</span>
                {badgeDescription && (
                    <p className='m-0 text-12 text-romance/80 leading-167 tracking-02'>{badgeDescription}</p>
                )}
            </div>
            {showLock && (
                <div className='pointer-events-none absolute top-4 right-4'>
                    <BadgeActionLockIcon />
                </div>
            )}
        </>
    );

    if (isClickable && onPress) {
        return (
            <ButtonRaw onClick={onPress} data-testid='level-card-button' className={className}>
                {content}
            </ButtonRaw>
        );
    }

    return (
        <div data-testid='level-card' className={className}>
            {content}
        </div>
    );
};

const MultiLevelBadgeDetails = ({badge, titleId}: MultiLevelBadgeDetailsProps) => {
    const {openLevel} = useBadgeDetailRoute();
    const notification = useJourneyNotification(badge);

    const {title, imageUrl, status, totalSteps, finishedSteps, journeyLevels, rewards, journeyId} = badge;
    const isEarned = status === BADGE_STATUSES.COMPLETED;

    const {multiLevelBadgeDetailsContent} = getBadgeDetailsContent();
    const {badgeBackgroundImage, remainingStepsPluralForms, selectRewardPluralForms, rewardDetailExternalLink} = multiLevelBadgeDetailsContent ?? {};

    const levels = journeyLevels ?? [];

    return (
        <div data-testid='journey-badge-details' className='flex flex-col gap-8 pb-app-4'>
            <BadgeDetailHeader
                title={title}
                titleId={titleId}
                imageUrl={imageUrl}
                backgroundImage={badgeBackgroundImage}
                notification={notification}
                isEarned={isEarned}
                badge={badge}
                totalSteps={totalSteps}
                finishedSteps={finishedSteps}
                remainingStepsPluralForms={remainingStepsPluralForms}
            />

            {levels.length > 0 && (
                <div data-testid='journey-levels-grid' className='grid grid-cols-2 gap-2 px-4'>
                    {levels.map((level, index) => {
                        const isLastOdd = index === levels.length - 1 && levels.length % 2 === 1;
                        const isFirst = index === 0;
                        const isLocked = level.status === BADGE_STATUSES.LOCKED;
                        const canOpen = !isLocked || isFirst;
                        return (
                            <LevelCard
                                key={`${level.badgeId}-${index}`}
                                level={level}
                                fullWidth={isLastOdd}
                                isFirst={isFirst}
                                onPress={canOpen ? () => openLevel(level.badgeId) : undefined}
                            />
                        );
                    })}
                </div>
            )}
            {rewards && rewards.length > 0 && (
                <BadgeRewardsList
                    rewards={rewards}
                    journeyId={journeyId}
                    selectRewardPluralForms={selectRewardPluralForms}
                    rewardDetailExternalLink={rewardDetailExternalLink}
                />
            )}
        </div>
    );
};

export default MultiLevelBadgeDetails;
